// TelcoTrack — Drawing Constants
//
// Nový model: barvy fází per výkres žijí ve vygenerovaných workflow datech (Barvy.xlsx →
// generated-workflows.ts). Tento soubor řeší viditelnost typů per výkres (Výkresy.xlsx „Role"),
// odvození editovatelných fází a delegaci barvy na model.

import { GENERATED_WORKFLOWS } from './object-workflow/generated-workflows';
import {
  getPhaseColorByCode,
  type GeneratedDrawingId,
} from './object-workflow/workflow-model';
import { OBJECT_TYPE_KONCOVYBOD } from './object-type-labels.constants';
import {
  canCreateMapObjects,
  hasAkviziceRole,
  hasObjectContentMutatorRole,
  ROLES,
} from './roles.constants';

// =============================================================================
// DRAWING IDS
// =============================================================================

export const DRAWING_IDS = {
  GENERAL: 'general',
  STAVBA: 'stavba',
  MONTAZ: 'montaz',
  ZAFUK: 'zafuk',
  OPTIKA: 'optika',
  /** Dedicated acquisition drawing — buildings only; not a Barvy.xlsx / GeneratedDrawingId layer. */
  AKVIZICE: 'akvizice',
  /**
   * Passport-photo drawing — same picker as field výkresy; not a Barvy.xlsx layer.
   * Photos stay on `/objects/pasportizace-photos` (not map objects).
   */
  PASPORTIZACE: 'pasportizace',
} as const;

export type DrawingId = typeof DRAWING_IDS[keyof typeof DRAWING_IDS];

/** Field + Obecný výkresy (Barvy.xlsx / Android export). Bez Akvizice / Pasportizace. */
export type WorkflowDrawingId = Exclude<
  DrawingId,
  typeof DRAWING_IDS.AKVIZICE | typeof DRAWING_IDS.PASPORTIZACE
>;

export function isAcquisitionDrawing(drawingId: string | null | undefined): boolean {
  return String(drawingId ?? '').trim() === DRAWING_IDS.AKVIZICE;
}

export function isPasportizaceDrawing(drawingId: string | null | undefined): boolean {
  return String(drawingId ?? '').trim() === DRAWING_IDS.PASPORTIZACE;
}

// =============================================================================
// OBJECT TYPES PER DRAWING — VIDITELNÉ (Výkresy.xlsx, list „Role")
// =============================================================================

const ALL_OBJECT_TYPES = [
  'VYKOP',
  'STAVAJICI',
  'ROZVADEC',
  'BUDOVA',
  'SPOJKOVISTE',
  'JAMA',
  'INFO',
] as const;

/** Jen budovy — výkres Akvizice (trasy / HP / RSU / INFO se nezobrazují). */
export const AKVIZICE_OBJECT_TYPES = ['BUDOVA'] as const;

export const OBJECT_TYPES_BY_DRAWING: Record<DrawingId, readonly string[]> = {
  [DRAWING_IDS.GENERAL]: ALL_OBJECT_TYPES,
  // Role: Stavba — VYKOP, STAVAJICI, ROZVADEC, BUDOVA, SPOJKOVISTE, JAMA, INFO
  [DRAWING_IDS.STAVBA]: ['VYKOP', 'STAVAJICI', 'ROZVADEC', 'BUDOVA', 'SPOJKOVISTE', 'JAMA', 'INFO'],
  // Role: Montáž
  [DRAWING_IDS.MONTAZ]: ['VYKOP', 'STAVAJICI', 'ROZVADEC', 'BUDOVA', 'SPOJKOVISTE', 'JAMA', 'INFO'],
  // Role: Záfuk — bez JAMA
  [DRAWING_IDS.ZAFUK]: ['VYKOP', 'STAVAJICI', 'ROZVADEC', 'BUDOVA', 'SPOJKOVISTE', 'INFO'],
  // Role: Optika — ROZVADEC, BUDOVA, INFO
  [DRAWING_IDS.OPTIKA]: ['ROZVADEC', 'BUDOVA', 'INFO'],
  [DRAWING_IDS.AKVIZICE]: AKVIZICE_OBJECT_TYPES,
  // Pasportizace — žádné mapové objekty (fotky jsou samostatná overlay vrstva)
  [DRAWING_IDS.PASPORTIZACE]: [],
};

/** Map role name (lowercase) to drawing id */
export const ROLE_TO_DRAWING: Record<string, DrawingId> = {
  stavba: DRAWING_IDS.STAVBA,
  montaz: DRAWING_IDS.MONTAZ,
  'montáž': DRAWING_IDS.MONTAZ,
  zafuk: DRAWING_IDS.ZAFUK,
  záfuk: DRAWING_IDS.ZAFUK,
  optika: DRAWING_IDS.OPTIKA,
  akvizice: DRAWING_IDS.AKVIZICE,
};

// =============================================================================
// EDITABLE PHASES PER DRAWING — odvozené z barev (fáze s barvou na výkrese)
// =============================================================================

type RoleDrawingId = Exclude<
  DrawingId,
  typeof DRAWING_IDS.GENERAL | typeof DRAWING_IDS.AKVIZICE | typeof DRAWING_IDS.PASPORTIZACE
>;

/**
 * Pro každý role-výkres a typ: kódy fází, které mají na daném výkrese definovanou barvu
 * (= fáze relevantní pro tento výkres). Slouží UI pro nabídku vytvoření objektu na výkrese.
 */
export const EDITABLE_PHASES_BY_DRAWING: Record<RoleDrawingId, Record<string, string[]>> = (() => {
  const roleDrawings: RoleDrawingId[] = [
    DRAWING_IDS.STAVBA,
    DRAWING_IDS.MONTAZ,
    DRAWING_IDS.ZAFUK,
    DRAWING_IDS.OPTIKA,
  ];
  const out = {} as Record<RoleDrawingId, Record<string, string[]>>;
  for (const d of roleDrawings) {
    out[d] = {};
    for (const wf of GENERATED_WORKFLOWS) {
      const codes = wf.phases
        .filter((p) => p.colors[d as GeneratedDrawingId] != null)
        .map((p) => p.code);
      if (!codes.length) continue;
      out[d][wf.objectType] = [...(out[d][wf.objectType] ?? []), ...codes];
    }
  }
  return out;
})();

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Hex barva fáze pro daný výkres (kód `WF.PHASE.N` nese workflow i index → barva je jednoznačná).
 * Vrací null, pokud se objekt na výkrese nezobrazuje (prázdná buňka v Barvy.xlsx).
 */
export function getPhaseColorForDrawing(
  drawingId: DrawingId | '' | undefined,
  _objectType: string,
  phaseCode: string,
): string | null {
  if (!drawingId || !phaseCode || isAcquisitionDrawing(drawingId)) return null;
  // Pasportizace má stejné barvy jako Obecný (není Barvy.xlsx vrstva).
  const colorDrawingId = isPasportizaceDrawing(drawingId)
    ? DRAWING_IDS.GENERAL
    : drawingId;
  return getPhaseColorByCode(phaseCode, colorDrawingId as GeneratedDrawingId);
}

/** Typy viditelné na výkrese (Akvizice = jen BUDOVA). */
export function getObjectTypesForDrawing(drawingId: DrawingId | string): readonly string[] {
  return OBJECT_TYPES_BY_DRAWING[drawingId as DrawingId] ?? [];
}

/** Je typ objektu viditelný na výkrese? */
export function isObjectTypeOnDrawing(drawingId: DrawingId, objectType: string): boolean {
  return getObjectTypesForDrawing(drawingId).includes(objectType);
}

/**
 * Může role na výkrese upravovat fázi? (2026-05-26: role-edit restrikce uvolněny — true pro
 * libovolný role-výkres; GENERAL a Akvizice jsou view-only.)
 */
export function canEditPhaseTo(
  drawingId: DrawingId,
  _objectType: string,
  _phaseCode: string,
): boolean {
  if (
    drawingId === DRAWING_IDS.GENERAL ||
    isAcquisitionDrawing(drawingId) ||
    isPasportizaceDrawing(drawingId)
  ) {
    return false;
  }
  return true;
}

/** Může uživatel s danými rolemi měnit fáze libovolného typu objektu? */
export function userCanEditObjectType(userRoles: string[] | undefined | null, objectType: string): boolean {
  const ot = String(objectType ?? '').trim();
  if (!ot) return false;
  return hasObjectContentMutatorRole(userRoles);
}

const ROLE_DRAWING_IDS: RoleDrawingId[] = [
  DRAWING_IDS.STAVBA,
  DRAWING_IDS.MONTAZ,
  DRAWING_IDS.ZAFUK,
  DRAWING_IDS.OPTIKA,
];

/**
 * Má uživatel roli mapovanou na daný výkres?
 * Role `editor` = oprávnění jako všechny field výkresy (ne Obecný).
 */
export function userHasRoleForDrawing(
  userRoles: string[] | undefined | null,
  drawingId: DrawingId,
): boolean {
  if (!drawingId || drawingId === DRAWING_IDS.GENERAL) return false;
  if (isPasportizaceDrawing(drawingId)) return false;
  if (isAcquisitionDrawing(drawingId)) return hasAkviziceRole(userRoles);
  const roles = (userRoles ?? []).map((r) => String(r).trim().toLowerCase());
  if (roles.includes('editor')) return true;
  return roles.some((r) => {
    const mapped = ROLE_TO_DRAWING[r];
    return mapped === drawingId || r === drawingId;
  });
}

/**
 * Výkres Akvizice na mapě — vidí všechny role.
 * Pořadí v pickeru řeší {@link getPickerDrawingsForUser} (po field výkresech, před Obecným).
 */
export function userCanViewAcquisitionDrawing(
  _userRoles?: string[] | undefined | null,
): boolean {
  return true;
}

/** Výkres Akvizice / Pasportizace vidí všechny role; ostatní výkresy beze změny. */
export function userCanViewDrawing(
  userRoles: string[] | undefined | null,
  drawingId: DrawingId | string,
): boolean {
  if (isAcquisitionDrawing(drawingId)) return userCanViewAcquisitionDrawing(userRoles);
  if (isPasportizaceDrawing(drawingId)) return true;
  return (DRAWING_IDS_LIST as readonly string[]).includes(drawingId);
}

export function getDrawingLabel(drawingId: DrawingId | string): string {
  return DRAWING_LABELS[drawingId as DrawingId] ?? String(drawingId);
}

/**
 * Picker výkresů — vždy: role/field výkresy + Pasportizace, pak Akvizice, Obecný poslední.
 * (Akvizice nikdy první ani za Obecným.)
 */
export function getPickerDrawingsForUser(
  _userRoles?: string[] | undefined | null,
): Array<{ id: DrawingId; label: string }> {
  const fieldThenGeneral = [...DRAWING_IDS_LIST];
  const generalIdx = fieldThenGeneral.indexOf(DRAWING_IDS.GENERAL);
  const fieldOnly =
    generalIdx >= 0
      ? [
          ...fieldThenGeneral.slice(0, generalIdx),
          ...fieldThenGeneral.slice(generalIdx + 1),
        ]
      : fieldThenGeneral;
  const ids: DrawingId[] = [...fieldOnly, DRAWING_IDS.AKVIZICE, DRAWING_IDS.GENERAL];
  return ids.map((id) => ({ id, label: getDrawingLabel(id) }));
}

/**
 * Smí uživatel potvrdit přechod do dané fáze? (web popup + BE validateStatusTransition)
 * Mutátoři obsahu (terén, editor, manager, sklad, admin) — ne viewer-like role.
 * Admin/superadmin řeší volající zvlášť pro revert mimo 1h okno.
 */
export function userCanCommitPhase(
  userRoles: string[] | undefined | null,
  objectType: string,
  phaseCode: string,
): boolean {
  const ot = String(objectType ?? '').trim();
  const phase = String(phaseCode ?? '').trim();
  if (!ot || !phase) return false;
  return hasObjectContentMutatorRole(userRoles);
}

/**
 * Seznam výkresů pro UI / Android export (role výkresy + Pasportizace, Obecný poslední).
 * Bez Akvizice (ta se vkládá v {@link getPickerDrawingsForUser}).
 */
export const DRAWING_IDS_LIST: Array<
  WorkflowDrawingId | typeof DRAWING_IDS.PASPORTIZACE
> = [
  DRAWING_IDS.STAVBA,
  DRAWING_IDS.MONTAZ,
  DRAWING_IDS.ZAFUK,
  DRAWING_IDS.OPTIKA,
  DRAWING_IDS.PASPORTIZACE,
  DRAWING_IDS.GENERAL,
];

/** Popisky výkresů pro UI + Android export. Field/Obecný + Akvizice + Pasportizace. */
export const DRAWING_LABELS: Record<DrawingId, string> = {
  [DRAWING_IDS.GENERAL]: 'Obecný',
  [DRAWING_IDS.STAVBA]: 'Stavba',
  [DRAWING_IDS.MONTAZ]: 'Montáž',
  [DRAWING_IDS.ZAFUK]: 'Záfuk',
  [DRAWING_IDS.OPTIKA]: 'Optika',
  [DRAWING_IDS.AKVIZICE]: 'Akvizice',
  [DRAWING_IDS.PASPORTIZACE]: 'Pasportizace',
};

// =============================================================================
// CREATE OBJECT MENU — bodové typy (pořadí + admin allowlist)
// =============================================================================

/**
 * Pořadí bodových typů v menu „Vytvořit objekt“ (dashboard / map context menu).
 * Lineární typy (VYKOP/STAVAJICI) se kreslí trasou, ne tímto menu.
 */
export const CREATABLE_POINT_TYPES_ORDER = [
  'SPOJKOVISTE',
  'JAMA',
  'INFO',
  'BUDOVA',
  'ROZVADEC',
] as const;

export type CreatablePointType = (typeof CREATABLE_POINT_TYPES_ORDER)[number];

/** Bodové typy, které smí na mapě zakládat jen admin/superadmin/import. */
export const ADMIN_ONLY_POINT_TYPES = ['BUDOVA', 'ROZVADEC'] as const;

export type AdminOnlyPointType = (typeof ADMIN_ONLY_POINT_TYPES)[number];

export function isAdminOnlyPointType(type: string | null | undefined): boolean {
  const t = String(type ?? '').trim().toUpperCase();
  return (ADMIN_ONLY_POINT_TYPES as readonly string[]).includes(t);
}

/**
 * Admin/superadmin/import smí z mapového context menu spustit volné kreslení VYKOP
 * (polyline → densifikované segmenty), pokud je VYKOP na daném výkrese viditelný.
 * `isAdmin` flag covers admin/superadmin from UI; pass true also when caller has import role.
 */
export function canAdminDrawVykopOnDrawing(
  drawingId: DrawingId | '' | undefined,
  isAdmin: boolean,
): boolean {
  if (!isAdmin) return false;
  const d = (drawingId && String(drawingId).trim()
    ? String(drawingId).trim()
    : DRAWING_IDS.GENERAL) as DrawingId;
  return isObjectTypeOnDrawing(d, 'VYKOP');
}

/**
 * Bodové typy nabízené v create menu na daném výkrese (bez role gate).
 * Role/admin filtrování zůstává v UI (web `openCreateObjectMenu` / KMP `getCreatablePointTypes`).
 */
export function getCreatablePointTypesOnDrawing(drawingId: DrawingId): CreatablePointType[] {
  const allowed = new Set(getObjectTypesForDrawing(drawingId));
  return CREATABLE_POINT_TYPES_ORDER.filter((t) => allowed.has(t));
}

function normalizeCreateRoles(roles: string[] | undefined | null): string[] {
  return (roles ?? []).map((r) => String(r).trim().toLowerCase()).filter(Boolean);
}

export function hasZafukCreateRole(roles: string[] | undefined | null): boolean {
  return normalizeCreateRoles(roles).some((r) => r === ROLES.ZAFUK || r === 'záfuk');
}

/**
 * Dedicated KONCOVYBOD create is retired — záfuk lives on spojkoviště.
 * Leftover API callers / tests keep this gate; always false.
 */
export function userCanCreateKoncovyBodZafuku(
  _roles: string[] | undefined | null,
  _isAdmin: boolean,
): boolean {
  return false;
}

/**
 * SSOT for map create-object menu (web `openCreateObjectMenu`, KMP `getCreatablePointTypes`).
 * Terén (stavba/montáž/záfuk/optika) + admin/import: SPOJKOVISTE / JAMA / INFO na výkrese.
 * BUDOVA / ROZVADEC: admin/superadmin/import (`isAdmin` flag or {@link canCreateMapObjects} with import).
 * KONCOVYBOD se nezakládá.
 */
export function userCanCreatePointType(
  type: string,
  options: {
    roles: string[] | undefined | null;
    drawingId: DrawingId;
    isAdmin: boolean;
  },
): boolean {
  const t = String(type ?? '').trim().toUpperCase();
  if (t === OBJECT_TYPE_KONCOVYBOD) return false;
  if (!(CREATABLE_POINT_TYPES_ORDER as readonly string[]).includes(t)) return false;
  if (!isObjectTypeOnDrawing(options.drawingId, t)) return false;

  const privilegedCreate =
    options.isAdmin === true ||
    normalizeCreateRoles(options.roles).some((r) => r === ROLES.IMPORT || r === ROLES.SUPERADMIN);

  if (isAdminOnlyPointType(t)) {
    return privilegedCreate;
  }
  if (t === 'INFO' || t === 'SPOJKOVISTE' || t === 'JAMA') {
    return privilegedCreate || canCreateMapObjects(options.roles);
  }
  return false;
}

/** Creatable point types for the current user on a drawing, in menu order. */
export function getCreatablePointTypesForUser(
  roles: string[] | undefined | null,
  drawingId: DrawingId,
  isAdmin: boolean,
): CreatablePointType[] {
  return CREATABLE_POINT_TYPES_ORDER.filter((t) =>
    userCanCreatePointType(t, { roles, drawingId, isAdmin }),
  );
}
